import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { API } from '@src/api';
import {
  FindhelpCategory,
  GetAllFavoritesResult,
  UniteUsService,
} from '@src/api.types';
import { TMemberInfo } from '@repo/utils';
import { CboSearchWrapperFindhelp } from '@src/common/CboSearchWrapperFindhelp/CboSearchWrapperFindhelp';
import { CboSearchWrapperUniteUs } from '@src/common/CboSearchWrapperUniteUs/CboSearchWrapperUniteUs';
import { LoadingSpinnerModal } from '@src/common/LoadingSpinnerModal/LoadingSpinnerModal';

import {
  loggedInAdvocateAtom,
  memberInContextAtom,
  identifiedNeedsAtom,
  vendorAtom,
} from '@src/global-state';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UniteUs } from '../UniteUs/UniteUs';
import { getFeatureFlagsFromStorage, saveVendor } from '@src/utills/storage';
import { CboSearchTopPanel } from '@src/common/CboSearchTopPanel/CboSearchTopPanel';

export type Vendor = 'Unite Us' | 'findhelp' | undefined;

export const recordsPerPageOptions: readonly [10, 25, 50] = [10, 25, 50];

export type CboSearchParams = {
  zipcode: string;
  category: string;
  categoryId: string | number | undefined;
  filters: string[];
  sortBy: 'relevance' | 'distance';
  recordsPerPage: (typeof recordsPerPageOptions)[number];
  page: number;
  distanceRadius: number;
};

export function searchParamsFromUrl(urlParams: {
  [key: string]: string;
}): CboSearchParams {
  return Object.entries(urlParams).reduce((acc, [key, value]) => {
    switch (key) {
      case 'zipcode':
        return {
          ...acc,
          zipcode: value,
        };
      case 'category':
        return {
          ...acc,
          category: value,
        };
      case 'filters':
        return {
          ...acc,
          filters: value.split(','),
        };
      case 'sortBy':
        return {
          ...acc,
          sortBy: value as 'relevance' | 'distance',
        };
      case 'categoryId':
        return {
          ...acc,
          categoryId: value,
        };
      case 'recordsPerPage':
        const recordsPerPage = parseInt(value, 10);
        return {
          ...acc,
          recordsPerPage:
            recordsPerPageOptions.includes(recordsPerPage as any) === false
              ? 25
              : (recordsPerPage as CboSearchParams['recordsPerPage']),
        };
      case 'page':
        return {
          ...acc,
          page: parseInt(value, 10),
        };
      default:
        return acc;
    }
  }, {}) as CboSearchParams;
}

export function SearchImproved(): React.JSX.Element {
  const { navigate, getSearchParams } = useRouter();

  // NOTE - Type error is incorrect.
  const [urlParams] = getSearchParams();

  // Memoized so the object reference is stable across re-renders.
  // Without this, any effect with featureFlagsFromStorage in its deps would
  // re-run on every render, causing duplicate API calls.
  const featureFlagsFromStorage = useMemo(() => getFeatureFlagsFromStorage(), []);

  const [loggedInAdvocate] = useAtom(loggedInAdvocateAtom);
  const member: TMemberInfo | null = useAtomValue(memberInContextAtom);
  const [identifiedNeeds, setIdentifiedNeeds] = useAtom(identifiedNeedsAtom);
  const [vendorCache, setVendorCache] = useAtom(vendorAtom);
  const [currentZipcode, setCurrentZipcode] = useState<string>('');
  const handleZipcodeChange = useCallback((zipcode: string) => {
    setCurrentZipcode(zipcode);
  }, []);
  if (member === null) {
    // The member being null on this page is an illegal state.
    // TODO - Redirect to member search.
    console.error('Member in context is null');
  }

  // On first render, read the search parameters from the URL.
  const [searchParams, setSearchParams] = useState<CboSearchParams>(() => {
    const entries = Object.fromEntries([...urlParams]);
    const params = searchParamsFromUrl(entries);
    return {
      zipcode: params.zipcode || (member?.zipCode ?? ''),
      category: params.category || '',
      categoryId: params.categoryId || '',
      filters: params.filters || [],
      sortBy: params.sortBy || 'relevance',
      recordsPerPage: params.recordsPerPage || 25,
      page: params.page || 1,
      distanceRadius: params.distanceRadius || 50,
    };
  });

  const [vendor, setVendor] = useState<Vendor>(undefined);
  const prevVendorRef = useRef<Vendor>(undefined);

  // When the vendor changes (not on initial load), clear search params that
  // are vendor-specific so stale categories/filters from the old vendor are not applied.
  useEffect(() => {
    if (prevVendorRef.current !== undefined && prevVendorRef.current !== vendor) {
      setSearchParams((prev) => ({
        ...prev,
        category: '',
        categoryId: '',
        filters: [],
        sortBy: 'relevance',
        page: 1,
      }));
    }
    prevVendorRef.current = vendor;
  }, [vendor]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mapsApiKey, setMapsApiKey] = useState<string>('');

  // Array of all the Findhelp categories
  const [findhelpCategoryData, setFindhelpCategoryData] = useState<
    FindhelpCategory[]
  >([]);

  const [uniteUsCategoryData, setUniteUsCategoryData] = useState<
    UniteUsService[]
  >([]);

  // Array of the user's favorite folders
  const [favoritesFolders, setFavoritesFolders] = useState<
    GetAllFavoritesResult[]
  >([]);

  // Flag that controls whether the Unite Us API CBO search page is enabled.
  // If it is not, we fall back to using the Unite Us iframe.
  const isUniteUsApiEnabled: boolean = useMemo(() => {
    const flag: any = featureFlagsFromStorage['agent_uniteus_api'];

    if (!flag) {
      console.error(
        'Unite Us API feature flag did not conform to expected shape'
      );
      return false;
    }

    return flag['enabled'] ?? false;
  }, [featureFlagsFromStorage]);

  // Process the list of favorites into a flat Set of favorited program ID's
  const favoritedProgramIds: Set<string> = useMemo(() => {
    return favoritesFolders
      .flatMap((folder) => {
        return folder.favoriteFolderPrograms.map((p) => {
          return p.programId;
        });
      })
      .reduce((acc, id) => {
        acc.add(id);
        return acc;
      }, new Set<string>());
  }, [favoritesFolders]);

  // Process the list of favorites into an array of only the folders names
  const favoritesFolderNames: string[] = useMemo(() => {
    return favoritesFolders.map((folder) => {
      return folder.folderName;
    });
  }, [favoritesFolders]);

  useEffect(() => {
    navigate({
      pathname: location.pathname,
      search: new URLSearchParams(searchParams as any).toString(),
    });
  }, [searchParams]);

  useEffect(() => {
    // Use cached vendor if the zipcode matches; otherwise fetch and cache.
    async function fetchVendor() {
      let response = vendorCache?.zipcode === searchParams.zipcode
        ? vendorCache.response
        : null;

      if (!response) {
        const req = {
          lineOfBusiness: member?.lineOfBusiness,
          zipcode: searchParams.zipcode,
        };
        response = await API.fetchProviderToUse(req);
        if (response?.status === 'success') {
          setVendorCache({ zipcode: searchParams.zipcode, response });
        }
      }

      if (!response || ('status' in response && response.status === 'error')) {
        console.error(
          `Could not fetch provider to use for ${member?.lob} member in ${searchParams.zipcode}`
        );
        setIsLoading(false);
        return;
      }
      const needsLength =
        identifiedNeeds !== null ? Object.keys(identifiedNeeds).length : 0;
      if (
        needsLength > 0 &&
        response.provider.name !== sessionStorage.getItem('UHC-CC-VENDOR')
      ) {
        setIdentifiedNeeds(null);
        saveVendor(response?.provider?.name);
      }
      if (response?.provider?.name) {
        setVendor(response.provider.name);
      }
      setIsLoading(false);
    }
    if (member != null && member !== undefined) {
      fetchVendor();
    }
  }, [searchParams.zipcode, member]);

  useEffect(() => {
    // Fetch maps API key based on feature flag
    async function fetchMapsApiKey() {
      const isGoogleMapsEnabled = (featureFlagsFromStorage as any)?.googlemaps_enabled?.enabled;

      if (isGoogleMapsEnabled) {
        // Fetch Google Maps API key
        const response = await API.getApiKey();
        if (!response || typeof response !== 'string') {
          console.error('Could not fetch Google Maps API key');
          return;
        }
        setMapsApiKey(response);
      } else {
        // Fetch Azure Maps credentials
        const response = await API.getAzureMapsCredentials();
        if (!response || !response.subscriptionKey) {
          console.error('Could not fetch Azure Maps credentials');
          return;
        }
        setMapsApiKey(response.subscriptionKey);
      }
    }

    if (mapsApiKey === '') {
      fetchMapsApiKey();
    }
  }, [featureFlagsFromStorage, mapsApiKey]);

  useEffect(() => {
    async function fetchFindhelpCategoryData() {
      const response = await API.getFindhelpCategories();
      if (
        !response ||
        ('status' in response && response.status === 'error') ||
        !Array.isArray(response.data)
      ) {
        console.error('Could not fetch Findhelp category data');
        return;
      }

      setFindhelpCategoryData(response.data);
    }

    if (findhelpCategoryData.length === 0 && vendor === 'findhelp') {
      fetchFindhelpCategoryData();
    }
  }, [vendor]);

  useEffect(() => {
    async function fetchUniteUsServices() {
      const response = await API.getUniteUsCategories();
      if (
        !response ||
        ('status' in response && response.status === 'error') ||
        !Array.isArray(response.data)
      ) {
        console.error('Could not fetch Unite Us services');
        return;
      }

      setUniteUsCategoryData(response.data);
    }

    if (uniteUsCategoryData.length === 0&& vendor === 'Unite Us' && isUniteUsApiEnabled) {
      fetchUniteUsServices();
    }
  }, [vendor, isUniteUsApiEnabled]);

  async function fetchFavoriteFolders() {
    const response = await API.getAllFavorites();
    if (
      !response ||
      ('status' in response && response.status === 'error') ||
      !Array.isArray(response.data)
    ) {
      console.error('Could not fetch favorite folders');
      return;
    }

    setFavoritesFolders(response.data);
  }

  useEffect(() => {
    fetchFavoriteFolders();
  }, []);

  if (!member) {
    console.warn('Rendered while the member was null!');
    return <span></span>;
  }

  return (
    <React.Fragment>
      {
        // Loading spinner if we're not done fetching provider
        isLoading && <LoadingSpinnerModal isLoading={isLoading} />
      }
      {/* Top panel - Search input fields & member info */}
      {uniteUsCategoryData && findhelpCategoryData && (
        <CboSearchTopPanel
          member={member}
          changedZipcode={handleZipcodeChange}
          vendor={vendor!}
          onVendorChange={(newVendor) => {
            setVendor(newVendor);
            // Only batch-clear search params when the vendor is actually switching
            // (not on the initial load where vendor is still undefined).
            if (vendor !== undefined && newVendor !== vendor) {
              setSearchParams((prev) => ({
                ...prev,
                category: '',
                categoryId: '',
                filters: [],
                sortBy: 'relevance',
                page: 1,
              }));
            }
          }}
          findhelpServices={findhelpCategoryData}
          uniteusServices={uniteUsCategoryData}
          defaultZipcode={searchParams.zipcode ?? undefined}
          defaultCategoryId={searchParams.categoryId ?? undefined}
          defaultCategoryLabel={searchParams.category ?? undefined}
          isUniteUsApiEnabled={isUniteUsApiEnabled}
          onClickNeed={async (need) => {
            const categoryResponse = await API.getCategoryForNeed({
              providerId: vendor === 'findhelp' ? '1001' : '1005',
              requestedNeedIds: [need.needId],
            });

            const categoryMapping = categoryResponse?.data?.[0] as {
              categoryId: string;
              category: string;
              needId: string;
            };

            if (!categoryMapping) {
              console.warn('Could not find category for need', need);
              return;
            }

            setSearchParams({
              ...searchParams,
              category: categoryMapping.category,
              page: 1,
              filters: [],
            });
          }}
          onSubmit={(category, zipcode) => {
            setSearchParams({
              ...searchParams,
              zipcode,
              category: category.label,
              categoryId: category.id ?? '',
              page: 1,
              filters: [],
            });
          }}
        />
      )}

      {vendor === 'findhelp' && (
        <CboSearchWrapperFindhelp
          azureMapsApiKey={mapsApiKey}
          searchParameters={searchParams}
          setSearchParameters={setSearchParams}
          member={member!}
          vendor={vendor!}
          userId={loggedInAdvocate?.user_name_id ?? ''}
          findhelpCategoryData={findhelpCategoryData}
          favoritedProgramIds={favoritedProgramIds}
          favoritesFolderNames={favoritesFolderNames}
          onSaveFavorite={async (folderName: string, programId: string) => {
            // Persist the favorite
            const resp = await API.saveFavorite(programId, folderName);
            if (!resp || ('status' in resp && resp.status === 'error')) {
              console.error('Could not save favorite', resp);
              return;
            }

            // Refresh the favorites list
            fetchFavoriteFolders();
          }}
          onRemoveFavorite={async (programId: string) => {
            // Make a list of all the folder ID's that the program appears in
            const folderIds = favoritesFolders.flatMap((folder) => {
              return folder.favoriteFolderPrograms
                .filter((fav) => {
                  return fav.programId === programId;
                })
                .map((fav) => {
                  return fav.folderId;
                });
            });

            // TODO - Re-assess this behavior. This seems unintuitive to users,
            //  and there should probably be a way to remove the program from only
            //  one specific favorites folder rather than an "all or nothing" approach.

            // Remove the program from each folder it appears in
            await Promise.all(
              folderIds.map((folderId) => {
                return API.deleteFavorite(folderId, programId);
              })
            );

            // Refresh the favorites list
            fetchFavoriteFolders();
          }}
          currentZipcode={currentZipcode}
        />
      )}

      {vendor === 'Unite Us' && isUniteUsApiEnabled && (
        <CboSearchWrapperUniteUs
          azureMapsApiKey={mapsApiKey}
          searchParameters={searchParams}
          setSearchParameters={setSearchParams}
          member={member!}
          vendor={vendor}
          favoritedProgramIds={favoritedProgramIds}
          favoritesFolderNames={favoritesFolderNames}
          onSaveFavorite={async (folderName: string, programId: string) => {
            // Persist the favorite
            const resp = await API.saveFavorite(programId, folderName);
            if (!resp || ('status' in resp && resp.status === 'error')) {
              console.error('Could not save favorite', resp);
              return;
            }

            // Refresh the favorites list
            fetchFavoriteFolders();
          }}
          onRemoveFavorite={(programId: string) => {
            // Make a list of all the folder ID's that the program appears in
            const folderIds = favoritesFolders.flatMap((folder) => {
              return folder.favoriteFolderPrograms
                .filter((fav) => {
                  return fav.programId === programId;
                })
                .map((fav) => {
                  return fav.folderId;
                });
            });

            // TODO - Re-assess this behavior. This seems unintuitive to users,
            //  and there should probably be a way to remove the program from only
            //  one specific favorites folder rather than an "all or nothing" approach.

            // Remove the program from each folder it appears in
            folderIds.forEach(async (folderId) => {
              await API.deleteFavorite(folderId, programId);
            });

            // Refresh the favorites list
            fetchFavoriteFolders();
          }}
          currentZipcode={currentZipcode}
        />
      )}
      {
        // Fallback to UniteUs running in an iframe when the feature flag is disabled
        vendor === 'Unite Us' && !isUniteUsApiEnabled && <UniteUs />
      }
    </React.Fragment>
  );
}
